import { useCallback, useEffect, useMemo, useState } from 'react'
import { ChevronRight, Folder, FolderPlus, HardDrive, Loader2, Search, Settings, Users } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { fetchWithAuth } from '@/lib/api/core'
import { createGoogleDriveFolder } from '@/lib/google-drive-sync-api'
import { isGoogleDriveReauthResponse, openGoogleDriveOAuthPopup } from '@/lib/google-drive-access'
import { toast } from 'sonner'
import { testIds } from '@/lib/test-ids'
import { openWorkspaceExtensionSettings } from '@/lib/open-settings'

type DriveNav = 'myDrive' | 'sharedDrives' | 'sharedDrive'

type BrowseItem = {
  id: string
  name: string
  mimeType: string
  isFolder?: boolean
}

type SharedDrive = { id: string; name: string }

type GoogleAccount = {
  id: string
  email: string | null
  label?: string | null
}

type Crumb = { id: string; name: string }

const FOLDER_MIME = 'application/vnd.google-apps.folder'

export function DrivePublishPicker({
  open,
  onOpenChange,
  documentTitle,
  organizationId,
  onPublish,
  publishing = false,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  documentTitle: string
  organizationId?: string | null
  onPublish: (parentFolderId: string, folderLabel: string) => void
  publishing?: boolean
}) {
  const [nav, setNav] = useState<DriveNav>('myDrive')
  const [sharedDriveId, setSharedDriveId] = useState<string | null>(null)
  const [sharedDriveName, setSharedDriveName] = useState<string | null>(null)
  const [folderId, setFolderId] = useState<string | undefined>()
  const [folders, setFolders] = useState<BrowseItem[]>([])
  const [sharedDrives, setSharedDrives] = useState<SharedDrive[]>([])
  const [breadcrumbs, setBreadcrumbs] = useState<Crumb[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [needsReauth, setNeedsReauth] = useState(false)
  const [truncated, setTruncated] = useState(false)
  const [filterQuery, setFilterQuery] = useState('')
  const [creatingFolder, setCreatingFolder] = useState(false)
  const [showNewFolder, setShowNewFolder] = useState(false)
  const [newFolderName, setNewFolderName] = useState('')
  const [accounts, setAccounts] = useState<GoogleAccount[]>([])
  const [workspaceTokenId, setWorkspaceTokenId] = useState<string | null>(null)
  const [accountsLoading, setAccountsLoading] = useState(false)
  const [switchingAccount, setSwitchingAccount] = useState(false)
  const [accountEpoch, setAccountEpoch] = useState(0)

  const reset = useCallback(() => {
    setNav('myDrive')
    setSharedDriveId(null)
    setSharedDriveName(null)
    setFolderId(undefined)
    setFolders([])
    setBreadcrumbs([])
    setError(null)
    setNeedsReauth(false)
    setTruncated(false)
    setFilterQuery('')
    setShowNewFolder(false)
    setNewFolderName('')
    setCreatingFolder(false)
    setAccounts([])
    setWorkspaceTokenId(null)
    setAccountsLoading(false)
    setSwitchingAccount(false)
  }, [])

  useEffect(() => {
    if (!open) reset()
  }, [open, reset])

  const loadSharedDrives = useCallback(async () => {
    if (!organizationId) return
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/drives?organizationId=${encodeURIComponent(organizationId)}`,
      )
      if (!res.ok) return
      const data = (await res.json()) as { drives?: SharedDrive[] }
      setSharedDrives(data.drives ?? [])
    } catch {
      setSharedDrives([])
    }
  }, [organizationId])

  const loadBrowse = useCallback(async () => {
    if (!organizationId || nav === 'sharedDrives') return
    setLoading(true)
    setError(null)
    setNeedsReauth(false)
    setTruncated(false)
    try {
      const corpus = nav === 'sharedDrive' ? 'drive' : 'myDrive'
      const params = new URLSearchParams({
        organizationId,
        corpus,
        foldersOnly: '1',
      })
      if (folderId) params.set('folderId', folderId)
      if (nav === 'sharedDrive' && sharedDriveId) params.set('driveId', sharedDriveId)

      const res = await fetchWithAuth(`/api/google-drive/browse?${params.toString()}`)
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; code?: string }
        if (isGoogleDriveReauthResponse(res, body)) {
          setNeedsReauth(true)
          setError(body.error ?? 'Google Drive token expired or revoked. Reconnect to continue.')
          setFolders([])
          return
        }
        throw new Error(body.error ?? `HTTP ${res.status}`)
      }
      const data = (await res.json()) as {
        items?: BrowseItem[]
        breadcrumbs?: Crumb[]
        truncated?: boolean
      }
      const folderItems = (data.items ?? []).filter(
        (i) => i.isFolder || i.mimeType === FOLDER_MIME,
      )
      setFolders(folderItems)
      setBreadcrumbs(data.breadcrumbs ?? [])
      setTruncated(Boolean(data.truncated))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to browse Drive')
      setFolders([])
    } finally {
      setLoading(false)
    }
  }, [organizationId, nav, folderId, sharedDriveId, accountEpoch])

  useEffect(() => {
    if (!open) return
    const onAuthSuccess = () => {
      setNeedsReauth(false)
      void loadBrowse()
    }
    window.addEventListener('google-drive-auth-success', onAuthSuccess)
    return () => window.removeEventListener('google-drive-auth-success', onAuthSuccess)
  }, [open, loadBrowse])

  const loadAccounts = useCallback(async () => {
    if (!organizationId) return
    setAccountsLoading(true)
    try {
      const res = await fetchWithAuth(
        `/api/google-drive/accounts?organizationId=${encodeURIComponent(organizationId)}`,
      )
      if (!res.ok) return
      const data = (await res.json()) as {
        accounts?: GoogleAccount[]
        workspaceTokenId?: string | null
      }
      setAccounts(data.accounts ?? [])
      setWorkspaceTokenId(data.workspaceTokenId ?? null)
    } catch {
      setAccounts([])
    } finally {
      setAccountsLoading(false)
    }
  }, [organizationId])

  useEffect(() => {
    if (!open) return
    void loadAccounts()
  }, [open, loadAccounts])

  const activeAccount = useMemo(() => {
    if (!accounts.length) return null
    if (workspaceTokenId) {
      const preferred = accounts.find((account) => account.id === workspaceTokenId)
      if (preferred) return preferred
    }
    return accounts[0]
  }, [accounts, workspaceTokenId])

  const handleAccountChange = useCallback(
    async (tokenId: string) => {
      if (!organizationId || tokenId === activeAccount?.id) return
      setSwitchingAccount(true)
      setError(null)
      try {
        const res = await fetchWithAuth('/api/google-drive/workspace-account', {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ tokenId, organizationId }),
        })
        if (!res.ok) {
          const err = (await res.json().catch(() => ({}))) as { error?: string }
          toast.error(typeof err.error === 'string' ? err.error : 'Could not switch Google account')
          return
        }
        setWorkspaceTokenId(tokenId)
        setNav('myDrive')
        setSharedDriveId(null)
        setSharedDriveName(null)
        setFolderId(undefined)
        setFilterQuery('')
        setShowNewFolder(false)
        setAccountEpoch((n) => n + 1)
        const next = accounts.find((account) => account.id === tokenId)
        toast.success(
          next?.email ? `Publishing as ${next.email}` : 'Workspace Google account updated',
        )
      } finally {
        setSwitchingAccount(false)
      }
    },
    [organizationId, activeAccount?.id, accounts],
  )

  const openAccountSettings = useCallback(() => {
    onOpenChange(false)
    openWorkspaceExtensionSettings('google-drive', organizationId ?? undefined)
  }, [onOpenChange, organizationId])

  useEffect(() => {
    if (!open) return
    if (nav === 'sharedDrives') {
      void loadSharedDrives()
      return
    }
    void loadBrowse()
  }, [open, nav, loadBrowse, loadSharedDrives])

  const filteredFolders = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    if (!q) return folders
    return folders.filter((f) => f.name.toLowerCase().includes(q))
  }, [folders, filterQuery])

  const filteredSharedDrives = useMemo(() => {
    const q = filterQuery.trim().toLowerCase()
    if (!q) return sharedDrives
    return sharedDrives.filter((d) => d.name.toLowerCase().includes(q))
  }, [sharedDrives, filterQuery])

  const selectedFolderId =
    folderId ??
    (nav === 'sharedDrive' && sharedDriveId ? sharedDriveId : null) ??
    (nav === 'myDrive' ? 'root' : null)

  const selectedLabel = (() => {
    if (folderId) {
      return breadcrumbs[breadcrumbs.length - 1]?.name ?? 'Selected folder'
    }
    if (nav === 'sharedDrive') return sharedDriveName ?? 'Shared drive'
    if (nav === 'myDrive') return 'My Drive'
    return 'Select a folder'
  })()

  const canPublish = Boolean(selectedFolderId) && nav !== 'sharedDrives' && !publishing
  const canCreateFolder =
    Boolean(selectedFolderId) &&
    nav !== 'sharedDrives' &&
    !publishing &&
    !creatingFolder &&
    Boolean(organizationId)

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim()
    if (!name || !selectedFolderId || !organizationId) return
    setCreatingFolder(true)
    setError(null)
    try {
      const folder = await createGoogleDriveFolder({
        name,
        parentFolderId: selectedFolderId,
        organizationId,
      })
      setNewFolderName('')
      setShowNewFolder(false)
      setFolderId(folder.id)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder')
    } finally {
      setCreatingFolder(false)
    }
  }, [newFolderName, selectedFolderId, organizationId])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg"
        data-testid={testIds.drivePublishPicker.root}
      >
        <DialogHeader>
          <DialogTitle>Publish to Drive</DialogTitle>
          <DialogDescription>
            Choose a folder for "{documentTitle || 'Untitled'}". A new Google Doc will be
            created and linked for Sync.
          </DialogDescription>
        </DialogHeader>

        <div
          className="flex items-end gap-2"
          data-testid={testIds.drivePublishPicker.accountRow}
        >
          <div className="min-w-0 flex-1">
            <p className="mb-1 text-xs text-muted-foreground">Google account</p>
            {accounts.length > 1 && activeAccount ? (
              <Select
                value={activeAccount.id}
                onValueChange={(tokenId) => void handleAccountChange(tokenId)}
                disabled={switchingAccount || publishing}
              >
                <SelectTrigger
                  className="h-8 w-full text-sm"
                  data-testid={testIds.drivePublishPicker.accountSelect}
                >
                  <SelectValue />
                  {switchingAccount ? <Loader2 className="h-3 w-3 animate-spin" /> : null}
                </SelectTrigger>
                <SelectContent className="z-[60]">
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.email ?? account.label ?? 'Unknown account'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <p className="truncate text-sm font-medium">
                {accountsLoading
                  ? 'Loading account...'
                  : (activeAccount?.email ?? 'No Google account connected')}
              </p>
            )}
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 shrink-0 px-2 text-xs"
            onClick={openAccountSettings}
            data-testid={testIds.drivePublishPicker.settingsLink}
          >
            <Settings className="mr-1 h-3.5 w-3.5" />
            Settings
          </Button>
        </div>

        <div
          className="flex gap-1 rounded-md border border-border bg-muted/30 p-0.5"
          data-testid={testIds.drivePublishPicker.corpusNav}
        >
          <button
            type="button"
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-sm px-2 py-1.5 text-xs font-medium',
              nav === 'myDrive'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => {
              setNav('myDrive')
              setSharedDriveId(null)
              setSharedDriveName(null)
              setFolderId(undefined)
              setShowNewFolder(false)
              setFilterQuery('')
            }}
          >
            <HardDrive className="h-3.5 w-3.5" />
            My Drive
          </button>
          <button
            type="button"
            className={cn(
              'flex flex-1 items-center justify-center gap-1 rounded-sm px-2 py-1.5 text-xs font-medium',
              nav === 'sharedDrives' || nav === 'sharedDrive'
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
            onClick={() => {
              setNav('sharedDrives')
              setSharedDriveId(null)
              setSharedDriveName(null)
              setFolderId(undefined)
              setShowNewFolder(false)
              setFilterQuery('')
            }}
          >
            <Users className="h-3.5 w-3.5" />
            Shared
          </button>
        </div>

        {(nav === 'myDrive' || nav === 'sharedDrive') && (
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-1 text-xs text-muted-foreground">
              <button
                type="button"
                className="hover:text-foreground hover:underline"
                onClick={() => {
                  setFolderId(undefined)
                  setFilterQuery('')
                }}
              >
                {nav === 'sharedDrive' ? sharedDriveName ?? 'Shared drive' : 'My Drive'}
              </button>
              {breadcrumbs.map((c) => (
                <span key={c.id} className="flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  <button
                    type="button"
                    className="hover:text-foreground hover:underline"
                    onClick={() => {
                      setFolderId(c.id)
                      setFilterQuery('')
                    }}
                  >
                    {c.name}
                  </button>
                </span>
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 px-2 text-xs"
              disabled={!canCreateFolder}
              onClick={() => {
                setShowNewFolder((v) => !v)
                setNewFolderName('')
              }}
              data-testid={testIds.drivePublishPicker.newFolderButton}
            >
              <FolderPlus className="mr-1 h-3.5 w-3.5" />
              New folder
            </Button>
          </div>
        )}

        {showNewFolder && nav !== 'sharedDrives' && (
          <div
            className="flex items-center gap-2"
            data-testid={testIds.drivePublishPicker.newFolderForm}
          >
            <Input
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              placeholder="Folder name"
              className="h-8 text-sm"
              disabled={creatingFolder}
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  void handleCreateFolder()
                }
                if (e.key === 'Escape') {
                  setShowNewFolder(false)
                  setNewFolderName('')
                }
              }}
              data-testid={testIds.drivePublishPicker.newFolderInput}
            />
            <Button
              type="button"
              size="sm"
              className="h-8"
              disabled={!newFolderName.trim() || creatingFolder}
              onClick={() => void handleCreateFolder()}
            >
              {creatingFolder ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : 'Create'}
            </Button>
          </div>
        )}

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filterQuery}
            onChange={(e) => setFilterQuery(e.target.value)}
            placeholder={
              nav === 'sharedDrives' ? 'Filter shared drives…' : 'Filter folders…'
            }
            className="h-8 pl-8 text-sm"
            data-testid={testIds.drivePublishPicker.filterInput}
          />
        </div>

        <div
          className="max-h-64 min-h-[160px] overflow-auto rounded-md border border-border"
          data-testid={testIds.drivePublishPicker.folderList}
        >
          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : error ? (
            <div className="px-3 py-6 text-center text-sm text-destructive">
              <p>{error}</p>
              {needsReauth ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="mt-3"
                  onClick={() => {
                    void openGoogleDriveOAuthPopup(organizationId).catch((err) => {
                      toast.error(err instanceof Error ? err.message : 'Failed to start Google Drive connection')
                    })
                  }}
                >
                  Reconnect Google Drive
                </Button>
              ) : null}
            </div>
          ) : nav === 'sharedDrives' ? (
            filteredSharedDrives.length === 0 ? (
              <p className="px-3 py-6 text-center text-sm text-muted-foreground">
                {filterQuery.trim() ? 'No shared drives match this filter' : 'No shared drives'}
              </p>
            ) : (
              filteredSharedDrives.map((drive) => (
                <button
                  key={drive.id}
                  type="button"
                  className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/40"
                  onClick={() => {
                    setNav('sharedDrive')
                    setSharedDriveId(drive.id)
                    setSharedDriveName(drive.name)
                    setFolderId(undefined)
                    setShowNewFolder(false)
                    setFilterQuery('')
                  }}
                >
                  <Users className="h-3.5 w-3.5 shrink-0 text-amber-500/80" />
                  <span className="truncate">{drive.name}</span>
                </button>
              ))
            )
          ) : filteredFolders.length === 0 ? (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">
              {filterQuery.trim()
                ? 'No folders match this filter'
                : 'No subfolders here. You can publish to this location.'}
            </p>
          ) : (
            filteredFolders.map((folder) => (
              <button
                key={folder.id}
                type="button"
                className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-accent/40"
                onClick={() => {
                  setFolderId(folder.id)
                  setFilterQuery('')
                }}
                data-testid={testIds.drivePublishPicker.folderItem(folder.id)}
              >
                <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{folder.name}</span>
              </button>
            ))
          )}
        </div>

        {truncated ? (
          <p className="text-[11px] text-amber-600 dark:text-amber-400">
            Folder list was truncated by Drive. Use filter after narrowing into a smaller
            parent, or create a new folder here.
          </p>
        ) : null}

        <p className="text-xs text-muted-foreground">
          Publish to: <span className="font-medium text-foreground">{selectedLabel}</span>
          {nav !== 'sharedDrives' && folders.length > 0 ? (
            <span className="text-muted-foreground/70">
              {' '}
              · {filterQuery.trim() ? `${filteredFolders.length} of ` : ''}
              {folders.length} folder{folders.length === 1 ? '' : 's'}
            </span>
          ) : null}
        </p>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={publishing || creatingFolder}
          >
            Cancel
          </Button>
          <Button
            onClick={() => {
              if (!selectedFolderId) return
              onPublish(selectedFolderId, selectedLabel)
            }}
            disabled={!canPublish || creatingFolder}
            data-testid={testIds.drivePublishPicker.publishButton}
          >
            {publishing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Publishing…
              </>
            ) : (
              'Publish'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
